import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, Button, TextField, Box, Typography, Grid, IconButton } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { RxCross2 } from "react-icons/rx";
import { useGetPortfolioSummaryQuery } from "store/slices/apis/portfolioSummarySlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import Select from "react-select";
import { useUpdateIrrMutation } from "store/slices/apis/irrViewTrackerSlice";



const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditIrrView = (props) => {
    const { open, handleClose, rowData, refetchCounts } = props;
    const { projectId: routeProjectId } = useParams();
    const dispatch = useDispatch();
    const [projectType, setProjectType] = useState(localStorage.getItem("projectType") || "all")

    const { data, error, isLoading, isFetching: isProjectFething, refetch } = useGetAllProjectsQuery(projectType);
    const { data: portfolioData, isFetching: portfolioLoader, isError: portfolioError } = useGetPortfolioSummaryQuery();

    const projectData = data?.data || [];
    const filteredProjectData = projectData.filter((data) => data.active == true);

    const projectOptions = (filteredProjectData || []).map(p => ({
        value: p.projectId,
        label: p.projectName,
        projectType: p.projectType,
    }));

    const portfolioOptions = (portfolioData?.data || []).map(p => ({
        value: p.portfolioName,
        label: p.portfolioName,
        projectType: p.portFolioType,
    }));

    const portfolioSelectOptions = useMemo(
        () => [
            // { projectType: "all", label: "All Portfolio" },
            ...portfolioOptions,
        ],
        [portfolioOptions]
    );

    const handlePortfolioChange = (option, setFieldValue) => {
        let projectType = option?.projectType || "all";
        setProjectType(projectType);
        localStorage.setItem("projectType", projectType)
        setFieldValue("product", option?.value || "");
    };


    const [updateIrr] = useUpdateIrrMutation();

    const [initialFormData, setInitialFormData] = useState({
        totalProjectCostUnderwriting: "",
        totalProjectCostCurrentActual: "",
        projectIrrPercent: "",
        equityIrrPercent: "",
        product: "",
        project: "",
        totalProjectPostCurrentActual: "",
        totalProjectPostUnerwriting: "",
        projectId: ""
    });

    useEffect(() => {
        if (rowData && open) {
            setInitialFormData({
                totalProjectCostUnderwriting: rowData.totalProjectCostUnderwriting || "",
                totalProjectCostCurrentActual: rowData.totalProjectCostCurrentActual || "",
                projectIrrPercent: rowData.projectIrrPercent || "",
                equityIrrPercent: rowData.equityIrrPercent || "",
                product: rowData?.product || "",
                project: rowData?.project || "",
                totalProjectPostCurrentActual: rowData?.totalProjectPostCurrentActual || "",
                totalProjectPostUnerwriting: rowData?.totalProjectPostUnerwriting || "",
                projectId: rowData?.projectId || "",
                // financialId: rowData.financialId || null,
            });
        }
    }, [rowData, open, routeProjectId]);

    const validationSchema = Yup.object({
        totalProjectCostUnderwriting: Yup.number()
            .typeError("Must be a number")
            .positive("Must be positive")
            .required("Total Project Cost (UnderWriting) is required"),
        totalProjectCostCurrentActual: Yup.number()
            .typeError("Must be a number")
            .positive("Must be positive")
            .nullable(),
        product: Yup.string().required("Product is required"),
        project: Yup.string().required("Project is required"),
        projectIrrPercent: Yup.number()
            .typeError("Must be a number")
            .min(0, "Cannot be negative")
            .max(100, "Cannot exceed 100")
            .nullable(),
        equityIrrPercent: Yup.number()
            .typeError("Must be a number")
            .min(0, "Cannot be negative")
            .max(100, "Cannot exceed 100")
            .nullable(),
    });

    const stringToDecimal = (value) => {
        if (value === "" || value === null || value === undefined) return null;

        const num = Number(value);
        if (isNaN(num)) return null;

        return Number(num.toFixed(2));
    };

    const emptyGUID = "00000000-0000-0000-0000-000000000000";
    
    const handleSubmit = async (values, { setSubmitting }) => {
        const reqBody = {
            irrViewId: rowData?.irrViewId,
            projectId: values?.projectId || "",
            totalProjectCostUnderwriting: stringToDecimal(values.totalProjectCostUnderwriting),
            totalProjectCostCurrentActual: stringToDecimal(values.totalProjectCostCurrentActual),
            projectIrrPercent: stringToDecimal(values.projectIrrPercent),
            equityIrrPercent: stringToDecimal(values.equityIrrPercent),
            product: values?.product || "",
            project: values?.project || "",
            totalProjectPostCurrentActual: values?.totalProjectPostCurrentActual || "",
            totalProjectPostUnerwriting: values?.totalProjectPostUnerwriting || "",
            isActive: true
        };

        console.log("Update payload:", reqBody);

        try {

            const response = await updateIrr({ body: reqBody }).unwrap();
            await new Promise(resolve => setTimeout(resolve, 500));

            openNotification("Financial details updated successfully", "success");
            setSubmitting(false);
            if (refetchCounts) refetchCounts();
            handleClose();
        } catch (error) {
            openNotification(error?.error ?? "Something went wrong", "error");
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: { color: type },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden"
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
                position: "relative",
            }}>
                <Typography sx={{
                    fontWeight: 600,
                    fontSize: "27px",
                    lineHeight: "100%",
                    color: "#140F36",
                    fontFamily: "Outfit, sans-serif"
                }}>
                    Edit IRR View
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => handleClose()}
                    sx={{
                        position: "absolute",
                        right: 50,
                        top: 8,
                        color: "#140F36",
                    }}
                >
                    <RxCross2 size={30} color="#140F36" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Formik
                                enableReinitialize={true}
                                initialValues={initialFormData}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ values, errors, touched, submitCount, handleChange, handleBlur, handleSubmit, setFieldTouched, setFieldValue, isSubmitting }) => (
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <Typography sx={{ mt: 1, mb: 0, fontSize: "22px", fontWeight: 500, fontFamily: "Outfit, sans-serif", color: "#0A0A0C" }}>
                                                    IRR View Details
                                                </Typography>
                                            </Grid>
                                            {routeProjectId == undefined &&
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography sx={labelTextStyle}>
                                                        Product
                                                    </Typography>

                                                    <Select
                                                        name="product"
                                                        options={portfolioSelectOptions}
                                                        value={
                                                            portfolioSelectOptions.find(
                                                                (opt) => opt.value === values.product
                                                            ) || null
                                                        }
                                                        onChange={(option) => handlePortfolioChange(option, setFieldValue)}
                                                        onBlur={() => setFieldTouched("product", true)}
                                                        placeholder="Select Portfolio"
                                                    />

                                                    {(touched.product || submitCount > 0) && errors.product && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.product}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            }

                                            { routeProjectId == undefined &&
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Typography sx={labelTextStyle}>
                                                        Project
                                                    </Typography>

                                                    <Select
                                                        name="project"
                                                        options={[{ value: emptyGUID, label: "All Projects" }, ...projectOptions]}
                                                        value={
                                                            [{ value: emptyGUID, label: "All Projects" }, ...projectOptions]
                                                                .find(opt => opt.value === values.projectId) || null
                                                        }
                                                        onChange={(option) => {
                                                            setFieldValue("projectId", option?.value);
                                                            setFieldValue("project", option?.label);
                                                        }}
                                                        onBlur={() => setFieldTouched("project", true)}
                                                        placeholder="Select Project"
                                                    />

                                                    {(touched.project || submitCount > 0) && errors.project && (
                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                            {errors.project}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            }
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Total Project Cost (INR Cr) UnderWriting
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="totalProjectCostUnderwriting"

                                                    value={values.totalProjectCostUnderwriting}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter total project cost (underwriting)"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.totalProjectCostUnderwriting && errors.totalProjectCostUnderwriting && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.totalProjectCostUnderwriting}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Phase (UnderWriting)
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="totalProjectPostUnerwriting"

                                                    value={values.totalProjectPostUnerwriting}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Phase"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.totalProjectPostUnerwriting &&
                                                    errors.totalProjectPostUnerwriting && (
                                                        <Typography
                                                            sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}
                                                        >
                                                            {errors.totalProjectPostUnerwriting}
                                                        </Typography>
                                                    )}
                                            </Grid>


                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Total Project Cost (INR Cr) Current/Actual
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="totalProjectCostCurrentActual"

                                                    value={values.totalProjectCostCurrentActual}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter current/actual total project cost"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.totalProjectCostCurrentActual && errors.totalProjectCostCurrentActual && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.totalProjectCostCurrentActual}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Phase (Current/Actual)
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="totalProjectPostCurrentActual"

                                                    value={values.totalProjectPostCurrentActual}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Phase"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.totalProjectPostCurrentActual &&
                                                    errors.totalProjectPostCurrentActual && (
                                                        <Typography
                                                            sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}
                                                        >
                                                            {errors.totalProjectPostCurrentActual}
                                                        </Typography>
                                                    )}
                                            </Grid>


                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Project IRR (%)
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="projectIrrPercent"

                                                    step="any"
                                                    value={values.projectIrrPercent}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Project IRR"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.projectIrrPercent && errors.projectIrrPercent && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.projectIrrPercent}
                                                    </Typography>
                                                )}
                                            </Grid>


                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <Typography sx={labelTextStyle}>
                                                    Equity IRR (%)
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    name="equityIrrPercent"

                                                    step="any"
                                                    value={values.equityIrrPercent}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter Equity IRR"
                                                    InputProps={{
                                                        sx: {
                                                            height: "40px",
                                                            borderRadius: "8px",
                                                            backgroundColor: "#fff",
                                                            "& input::placeholder": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#5F5F5F",
                                                                fontFamily: "Outfit, sans-serif",
                                                                opacity: 1,
                                                            },
                                                            "& input": {
                                                                fontWeight: 400,
                                                                fontSize: "16px",
                                                                lineHeight: "100%",
                                                                letterSpacing: "0.16px",
                                                                color: "#0A0A0C",
                                                                fontFamily: "Outfit, sans-serif",
                                                                "&:-webkit-autofill": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:hover": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                                "&:-webkit-autofill:focus": {
                                                                    WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                    WebkitTextFillColor: "#0A0A0C",
                                                                },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {touched.equityIrrPercent && errors.equityIrrPercent && (
                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                        {errors.equityIrrPercent}
                                                    </Typography>
                                                )}
                                            </Grid>

                                            {/* Action Buttons */}
                                            <Grid size={12}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                        gap: 2,
                                                        mt: 2,
                                                    }}
                                                >
                                                    <Button onClick={handleClose}
                                                        sx={{
                                                            textTransform: "none",
                                                            color: "#F15744",
                                                            fontSize: "14px",
                                                            lineHeight: "100%",
                                                            fontFamily: "Outfit, sans-serif",
                                                            borderRadius: "25px",
                                                            fontWeight: 500,
                                                            px: "20px",
                                                            py: "15px",
                                                        }}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        disabled={isSubmitting}
                                                        sx={{
                                                            backgroundColor: "#00344C",
                                                            borderRadius: "25px",
                                                            textTransform: "none",
                                                            fontWeight: 500,
                                                            fontSize: "14px",
                                                            lineHeight: "100%",
                                                            fontFamily: "Outfit, sans-serif",
                                                            color: "#FFFFFF",
                                                            px: "20px",
                                                            py: "15px",
                                                            '&:disabled': {
                                                                backgroundColor: '#cccccc',
                                                            }
                                                        }}
                                                    >
                                                        {isSubmitting ? "Updating..." : "Update"}
                                                    </Button>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </form>
                                )}
                            </Formik>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default EditIrrView;